import { useMemo } from 'react';

import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useSearch } from '@tanstack/react-router';
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { isEmpty } from 'es-toolkit/compat';

import { priorityGroupsNames } from '../../common';
import { OfflineSalesOptimizerStrategy } from '../../schemas';
import { MetricsStrategy, PriorityGroup, PriorityGroupSize } from '../../types';

import {
  useGetInsightsFiltersQuery,
  useGetInsightsMetricsQuery,
} from '@/api/endpoints/offlineSalesOptimizer';
import { Table } from '@/shared/components/table';
import { useGlobalFilterKeys } from '@/shared/hooks';

const columnHelper = createColumnHelper<PriorityGroupSize>();

const columns = [
  columnHelper.accessor('priorityGroup', {
    header: () => (
      <Typography variant='body3' component='span'>
        Priority Group
      </Typography>
    ),
    cell: (info) => (
      <Typography variant='subtitleNumbers' component='span'>
        {priorityGroupsNames[info.getValue()]}
      </Typography>
    ),
    size: 150,
  }),

  columnHelper.accessor('areaCount', {
    header: () => (
      <Typography variant='body3' component='span'>
        Area Count
      </Typography>
    ),
    cell: (info) => (
      <Typography variant='subtitleNumbers' component='span'>
        {new Intl.NumberFormat('en-US', {
          maximumFractionDigits: 0,
          style: 'decimal',
        }).format(info.getValue())}
      </Typography>
    ),
    size: 150,
  }),

  columnHelper.accessor('populationSize', {
    header: () => (
      <Typography variant='body3' component='span'>
        Population Size
      </Typography>
    ),
    cell: (info) => (
      <Typography variant='subtitleNumbers' component='span'>
        {new Intl.NumberFormat('en-US', {
          maximumFractionDigits: 0,
          style: 'decimal',
        }).format(info.getValue())}
      </Typography>
    ),
    size: 150,
  }),
  columnHelper.accessor('populationPercentage', {
    header: () => (
      <Typography variant='body3' component='span'>
        Population %
      </Typography>
    ),
    cell: (info) => (
      <Typography variant='subtitleNumbers' component='span'>
        {info.getValue()}%
      </Typography>
    ),
    size: 150,
  }),
];

const strategyMetricsKeyMap: Record<
  OfflineSalesOptimizerStrategy,
  MetricsStrategy
> = {
  maximize_roi: 'maximizeRoi',
  maximize_market_share_minimize_decay: 'maximizeMarketShareMinimizeDecay',
  maximize_market_share: 'maximizeMarketShare',
  velocity: 'velocity',
};

const PriorityGroups = () => {
  const { country, brand, strategy, mode } = useSearch({ strict: false });

  const { globalFilterKeys, isGlobalFilterReady } = useGlobalFilterKeys();
  const {
    isLoading: isLoadingFilters,
    isFetching: isFetchingFilters,
    isError: isErrorFilters,
  } = useGetInsightsFiltersQuery(globalFilterKeys, {
    skip: !isGlobalFilterReady,
  });

  const {
    data: geoInsightsMetrics,
    isLoading: isLoadingMetrics,
    isFetching: isFetchingMetrics,
    isError: isErrorMetrics,
  } = useGetInsightsMetricsQuery(
    {
      country: country ?? '',
      brand: brand ?? '',
      mode: mode ?? '',
      ...globalFilterKeys,
    },
    {
      skip: !isGlobalFilterReady || !country || !brand || !mode,
    }
  );

  const sortedPriorityGroupSizes = useMemo(() => {
    const strategyKey = strategyMetricsKeyMap[strategy ?? 'maximize_roi'];

    if (isEmpty(geoInsightsMetrics?.priorityGroupSizes[strategyKey])) {
      return [];
    }

    const priorityGroupOrder: PriorityGroup[] = ['L', 'M', 'H', 'VH'];

    return [
      ...(geoInsightsMetrics?.priorityGroupSizes[strategyKey] ?? []),
    ].sort(
      (a, b) =>
        priorityGroupOrder.indexOf(a.priorityGroup) -
        priorityGroupOrder.indexOf(b.priorityGroup)
    );
  }, [strategy, geoInsightsMetrics?.priorityGroupSizes]);

  const hasRequiredFilters = !!country && !!brand && !!mode;
  const table = useReactTable<PriorityGroupSize>({
    state: {
      pagination: { pageIndex: -1, pageSize: -1 },
    },
    data: hasRequiredFilters ? sortedPriorityGroupSizes : [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: false,
    enableSorting: false,
  });

  return (
    <Paper
      elevation={0}
      sx={[
        (theme) => ({
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: theme.customShadows.sm,
        }),
        (theme) =>
          theme.applyStyles('dark', {
            border: 0,
            boxShadow: 'none',
          }),
      ]}
    >
      <Box p={3}>
        <Typography variant='h6' component='h2'>
          Priority Group Sizes
        </Typography>
        <Typography variant='body2' color='text.secondary' pt={0.5}>
          Precision area count and total population size by priority group.
        </Typography>
      </Box>

      <Divider />

      <Table
        table={table}
        isLoading={isLoadingFilters || isLoadingMetrics}
        isFetching={isFetchingFilters || isFetchingMetrics}
        isError={isErrorFilters || isErrorMetrics}
        tableLabel='Priority Group Sizes Table'
        errorText='There was an error loading the data. Please try again.'
        emptyText='No data available.'
      />
    </Paper>
  );
};

export default PriorityGroups;
